import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TimeField } from "@/components/TemporalField";
import { PageHeader } from "../components/PageHeader";

const DAY_LABELS = [
  { key: "monday", label: "Lunedì" },
  { key: "tuesday", label: "Martedì" },
  { key: "wednesday", label: "Mercoledì" },
  { key: "thursday", label: "Giovedì" },
  { key: "friday", label: "Venerdì" },
  { key: "saturday", label: "Sabato" },
  { key: "sunday", label: "Domenica" },
];

function isValidBusinessHoursResponse(data) {
  const workingHours = data?.working_hours;
  const timePattern = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
  return Boolean(
    data?.timezone === "Europe/Rome" &&
    workingHours && typeof workingHours === "object" && !Array.isArray(workingHours) &&
    Object.keys(workingHours).length === DAY_LABELS.length &&
    DAY_LABELS.every((day) => {
      const intervals = workingHours[day.key];
      return Object.prototype.hasOwnProperty.call(workingHours, day.key) &&
        Array.isArray(intervals) && intervals.length <= 1 &&
        intervals.every((interval) =>
          interval && typeof interval.start === "string" && typeof interval.end === "string" &&
          timePattern.test(interval.start) && timePattern.test(interval.end) &&
          interval.start < interval.end
        );
    })
  );
}

function normalizeWorkingHours(workingHours) {
  return DAY_LABELS.reduce((accumulator, day) => {
    const intervals = Array.isArray(workingHours?.[day.key]) ? workingHours[day.key] : [];
    const firstInterval = intervals[0];
    accumulator[day.key] = {
      enabled: intervals.length > 0,
      start: firstInterval?.start ?? "09:00",
      end: firstInterval?.end ?? "19:00",
    };
    return accumulator;
  }, {});
}

function buildPayload(days) {
  return DAY_LABELS.reduce((accumulator, day) => {
    const currentDay = days[day.key];
    accumulator[day.key] = currentDay.enabled
      ? [{ start: currentDay.start, end: currentDay.end }]
      : [];
    return accumulator;
  }, {});
}

function getLoadErrorMessage(error) {
  if (error?.response?.status === 403) {
    return "Accesso riservato al titolare.";
  }
  return "Impossibile caricare gli orari di lavoro.";
}

function getSaveErrorMessage(error) {
  if (error?.response?.status === 403) {
    return "Accesso riservato al titolare.";
  }
  if (error?.response?.status === 400 || error?.response?.status === 422) {
    return "Controlla gli orari inseriti prima di salvare.";
  }
  return "Impossibile salvare gli orari di lavoro.";
}

export function BusinessHoursPage() {
  const [days, setDays] = useState(() => normalizeWorkingHours());
  const [timezone, setTimezone] = useState("Europe/Rome");
  const [updatedAt, setUpdatedAt] = useState("");
  const [loadStatus, setLoadStatus] = useState("loading");
  const loading = loadStatus === "loading";
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const loadGeneration = useRef(0);

  const loadBusinessHours = useCallback(async () => {
    const generation = ++loadGeneration.current;
    setLoadStatus("loading");
    setError("");
    try {
      const response = await api.get("/business-hours");
      if (generation !== loadGeneration.current) return;
      if (!isValidBusinessHoursResponse(response.data)) {
        throw new Error("Invalid business hours response");
      }
      setDays(normalizeWorkingHours(response.data?.working_hours));
      setTimezone(response.data?.timezone || "Europe/Rome");
      setUpdatedAt(response.data?.updated_at || "");
      setError("");
      setLoadStatus("success");
    } catch (loadError) {
      if (generation !== loadGeneration.current) return;
      const message = getLoadErrorMessage(loadError);
      setError(message);
      setLoadStatus("error");
      toast.error(message);
    }
  }, []);

  useEffect(() => {
    loadBusinessHours();
    return () => { loadGeneration.current += 1; };
  }, [loadBusinessHours]);

  const lastUpdatedLabel = useMemo(() => {
    if (!updatedAt) {
      return "Mai aggiornati";
    }
    const parsed = new Date(updatedAt);
    if (Number.isNaN(parsed.getTime())) {
      return updatedAt;
    }
    return parsed.toLocaleString("it-IT");
  }, [updatedAt]);

  const updateDay = (dayKey, field, value) => {
    setDays((current) => ({
      ...current,
      [dayKey]: {
        ...current[dayKey],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (loadStatus !== "success" || saving) return;
    setSaving(true);
    try {
      const response = await api.patch("/business-hours", {
        timezone,
        working_hours: buildPayload(days),
      });
      setDays(normalizeWorkingHours(response.data?.working_hours));
      setTimezone(response.data?.timezone || "Europe/Rome");
      setUpdatedAt(response.data?.updated_at || "");
      toast.success("Orari di lavoro salvati.");
    } catch (saveError) {
      toast.error(getSaveErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="business-hours-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <PageHeader
          title="Orari di lavoro"
          subtitle="Definisci la fascia operativa usata da disponibilità, appuntamenti e blocchi."
          titleTestId="business-hours-title"
          subtitleTestId="business-hours-subtitle"
        />
        <div className="text-sm text-slate-500" data-testid="business-hours-updated-at">
          Ultimo aggiornamento: {lastUpdatedLabel}
        </div>
      </div>

      <section className="bg-white border border-slate-200 rounded-sm p-4 md:p-5" data-testid="business-hours-summary">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-base font-semibold section-title">Timezone</h3>
            <p className="text-sm text-slate-500">{timezone}</p>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={loadStatus !== "success" || saving}
            className="rounded-sm bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300"
            data-testid="business-hours-save-button"
          >
            {saving ? "Salvataggio..." : "Salva orari"}
          </button>
        </div>
      </section>

      {loading && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 text-sm text-slate-500" data-testid="business-hours-loading">
          Caricamento orari...
        </div>
      )}

      {!loading && error && (
        <div className="bg-white border border-red-200 rounded-sm p-6 text-sm text-red-600" data-testid="business-hours-error">
          <p>{error}</p>
          <button
            type="button"
            onClick={loadBusinessHours}
            className="mt-3 rounded-sm border border-red-200 px-4 py-2 font-medium"
            data-testid="business-hours-retry-button"
          >
            Riprova
          </button>
        </div>
      )}

      {!loading && !error && (
        <section className="space-y-3" data-testid="business-hours-days">
          {DAY_LABELS.map((day) => {
            const currentDay = days[day.key];
            return (
              <div
                key={day.key}
                className="bg-white border border-slate-200 rounded-sm p-4"
                data-testid={`business-hours-day-${day.key}`}
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-base font-semibold section-title">{day.label}</h3>
                    <p className="text-sm text-slate-500">
                      {currentDay.enabled ? "Giorno operativo" : "Chiuso"}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={currentDay.enabled}
                      onChange={(event) => updateDay(day.key, "enabled", event.target.checked)}
                      data-testid={`business-hours-toggle-${day.key}`}
                    />
                    Aperto
                  </label>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <TimeField label="Inizio" value={currentDay.start} disabled={!currentDay.enabled} onChange={(event) => updateDay(day.key, "start", event.target.value)} data-testid={`business-hours-start-${day.key}`} />
                  <TimeField label="Fine" value={currentDay.end} disabled={!currentDay.enabled} onChange={(event) => updateDay(day.key, "end", event.target.value)} data-testid={`business-hours-end-${day.key}`} />
                </div>
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}
